/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ImageHeading from "../components/ui/ImageHeading";
import { cn } from "@/lib/utils";
import { X } from "lucide-react";
import DepartmentFacultyCard from "../components/ui/Card/DepartmentFacultyCard";
import useAxiosPublic from "../hooks/useAxiosPublic";
import { useQuery } from "@tanstack/react-query";
import LoadingPage from "../components/Layout/LoadingPage";

const Faculty = () => {
  const [activeSchool, setActiveSchool] = useState(1);
  const [selectedFaculty, setSelectedFaculty] = useState(null);
  const axiosPublic = useAxiosPublic();

  const {
    data: facultyApiData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["faculty"],
    queryFn: async () => {
      const res = await axiosPublic.get("/discover-zums/faculty");
      return res.data?.data;
    },
    onSuccess: (data) => {
      if (!activeSchool && data?.faculties?.length > 0) {
        setActiveSchool(data.faculties[0].id);
      }
    },
  });

  if (isLoading) {
    return <LoadingPage />;
  }

  if (error) {
    return (
      <div className="text-center py-20 text-red-500">Error loading data</div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200">
      {/* Banner */}
      <ImageHeading
        heading={facultyApiData?.banner?.title}
        imageUrl={facultyApiData?.banner?.media_url}
      />

      <div className="container px-4 py-6 mx-auto sm:py-12 sm:px-6 lg:px-8">
        {/* School Tabs */}
        <div className="mb-8 flex justify-center overflow-x-auto no-visible-scrollbar">
          <div className="px-8 py-4 bg-white rounded-md  lg:rounded-full w-fit flex-col lg:flex-row flex gap-4">
            {facultyApiData.faculties.map((school) => (
              <button
                key={school.id}
                onClick={() => setActiveSchool(school.id)}
                className={cn(
                  "relative px-4 py-2 rounded-full transition-all",
                  activeSchool === school.id
                    ? "bg-primary text-white"
                    : "text-black hover:bg-gray-100",
                )}
              >
                {school.name}
              </button>
            ))}
          </div>
        </div>

        {/* Faculty Grid */}
        <motion.div
          // layout
          className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4"
        >
          <>
            {facultyApiData.faculties
              .find((school) => school.id === activeSchool)
              ?.faculty.map((faculty) => (
                <div
                  key={faculty.id}
                  onClick={() => setSelectedFaculty(faculty)}
                  className="cursor-pointer"
                >
                  <DepartmentFacultyCard {...faculty} />
                </div>
              ))}
          </>
        </motion.div>

        {/* Faculty Modal */}
        {/* <AnimatePresence>
          {selectedFaculty && (
            <FacultyModal
              faculty={selectedFaculty}
              onClose={() => setSelectedFaculty(null)}
            />
          )}
        </AnimatePresence> */}
      </div>
    </div>
  );
};

const FacultyModal = ({ faculty, onClose }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 mt-16"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto"
      >
        <div className="relative">
          <button
            onClick={onClose}
            className="absolute p-2 rounded-full right-4 top-4 bg-white/80 hover:bg-white"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="w-full aspect-square overflow-hidden">
            <img
              src={faculty.image_url || "/placeholder.jpg"}
              alt={faculty.name}
              className="object-cover w-full h-full"
            />
          </div>
          <div className="p-6">
            <h2 className="text-2xl font-bold">{faculty.name}</h2>
            <p className="font-medium text-primary-600">
              {faculty.designation}
            </p>
            <p className="mb-4 text-gray-600">{faculty.department}</p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default Faculty;
